import * as React from "react";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

type DownloadButtonProps = {
  onExcel: () => void;
  onCsv: () => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  size?: "small" | "medium" | "large";
};

export default function DownloadButton({
  onExcel,
  onCsv,
  disabled = false,
  loading = false,
  label = "Download",
  size = "small",
}: DownloadButtonProps) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // --- Keep these in sync with your Take Action button ---
  const CONTROL_HEIGHT = 30;       // matches your Take Action default height
  const PILL_RADIUS = 28;          // matches styles={{ borderRadius: 28 }}
  const ENABLED_BG = "black";
  const ENABLED_COLOR = "white";
  const ENABLED_BORDER = "1px solid black";

  // Disabled states copied from your ButtonComponent
  const DISABLED_BG = "rgba(0, 0, 0, 0.12)";
  const DISABLED_COLOR = "rgba(0, 0, 0, 0.26)";

  const handlePrimaryClick = () => {
    if (disabled || loading) return;
    onExcel?.();
  };
  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
  };
  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && event && "target" in event) {
      const tgt = event.target as HTMLElement;
      if (anchorRef.current.contains(tgt)) return;
    }
    setOpen(false);
  };
  const handleExcel = () => {
    onExcel?.();
    setOpen(false);
  };
  const handleCsv = () => {
    onCsv?.();
    setOpen(false);
  };

  const groupWidth = anchorRef.current?.offsetWidth ?? undefined;

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        size={size}
        aria-label="Download split button"
        disableElevation
        sx={{
          height: CONTROL_HEIGHT,
          borderRadius: PILL_RADIUS,
          overflow: "hidden",
          backgroundColor: disabled ? DISABLED_BG : ENABLED_BG,
          border: disabled ? "none" : ENABLED_BORDER,

          // unify both halves
          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 400,
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            lineHeight: 1,
            padding: "0 12px",
            fontSize: "12px",
            color: disabled ? DISABLED_COLOR : ENABLED_COLOR,
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
              color: "white",
              borderColor:'white'
            },

            "&.Mui-disabled": {
              opacity: 1, // we already set specific disabled colors
              backgroundColor: "transparent",
              WebkitTextFillColor: DISABLED_COLOR,
            },
          },

          // Soft, centered divider between the two buttons
          "& .MuiButtonGroup-grouped:not(:first-of-type)": {
            marginLeft: 0,
            border: "none",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: "1px",
              height: Math.max(CONTROL_HEIGHT - 12, 14), // small inset
              backgroundColor: disabled
                ? "rgba(0,0,0,0.16)"
                : "rgba(255,255,255,0.65)",
            },
},

          "&:hover": {
            borderColor:'white',
          },
        }}
      >
        <Button
          onClick={handlePrimaryClick}
          disabled={disabled}
          sx={{
            minWidth: 110,
            borderStyle:'none',
            justifyContent: "center",
          }}
        >
          {loading ? "Downloading…" : label}
        </Button>

        <Button
          size={size}
          aria-controls={open ? "download-split-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={disabled || loading}
          sx={{
            minWidth: 36,
            px: 0.5,
            "& .MuiSvgIcon-root": {
              color: disabled ? DISABLED_COLOR : ENABLED_COLOR,
              fontSize: 18, // close to your PlayCircleIcon 16px
            },
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>

      <Popper
        sx={{ zIndex: 1300 }}
        open={open}
        anchorEl={anchorRef.current}
        transition
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
            <Paper
              elevation={0}
              sx={{
                width: groupWidth,
                borderRadius: 1,
                border: "1px solid",
                borderColor: theme.palette.divider,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem dense>
                  <MenuItem onClick={handleExcel} disabled={disabled}>
                    Excel
                  </MenuItem>
                  <MenuItem onClick={handleCsv} disabled={disabled}>
                    CSV
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}